import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faMagnifyingGlass } from "@fortawesome/pro-light-svg-icons";
import { useV2ListAccountsQuery, useV2SwitchAccountMutation } from "@generated";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";

interface SwitchAccountModalProps {
  show: boolean;
  onClose: () => void;
  /** Slug of the account the user is currently signed in to. */
  currentAccountSlug?: string;
}

// The API scopes /v2/accounts for us: site admins get every account, everyone
// else gets their memberships. There is deliberately no privilege branch here.
const PAGE_SIZE = 250;
const SEARCH_DEBOUNCE_MS = 300;

/** Unsearched account list — shared with UserMenuCard so both hit one cache entry. */
export const ACCOUNT_LIST_ARGS = {
  filter: { status: ["active"] as ["active"] },
  page: { size: PAGE_SIZE },
};

const SwitchAccountModal: React.FC<SwitchAccountModalProps> = ({
  show,
  onClose,
  currentAccountSlug,
}) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(search),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isFetching } = useV2ListAccountsQuery(
    debouncedSearch
      ? {
          ...ACCOUNT_LIST_ARGS,
          filter: { ...ACCOUNT_LIST_ARGS.filter, name: debouncedSearch },
        }
      : ACCOUNT_LIST_ARGS,
    { skip: !show }
  );

  const [switchAccount] = useV2SwitchAccountMutation();

  const accounts = data?.data ?? [];

  const handleSwitch = (slug?: string) => {
    if (!slug || slug === currentAccountSlug) return;
    setError(null);
    switchAccount({ id: slug })
      .unwrap()
      .then(() => {
        // Full page load so Rails, the Redux slug slice, and the RTK Query
        // cache all come back under the newly selected account.
        window.location.assign("/projects");
      })
      .catch(() => {
        setError("Could not switch accounts. Please try again.");
      });
  };

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title className="ssModalTitle">Switch Account</Modal.Title>
      </Modal.Header>
      <Modal.Body className="ssModalBody">
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        <div className="input-group mb-3">
          <div className="input-group-prepend">
            <span className="input-group-text">
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </span>
          </div>
          <input
            type="text"
            className="form-control"
            placeholder="Search accounts"
            aria-label="Search accounts"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {isFetching ? (
          <ScopeStackSpinner />
        ) : accounts.length === 0 ? (
          <p className="text-muted">No accounts match your search.</p>
        ) : (
          <div
            className="list-group"
            style={{ maxHeight: 360, overflowY: "auto" }}
          >
            {accounts.map((account) => {
              const slug = account.attributes?.slug ?? account.id;
              const isCurrent = slug === currentAccountSlug;

              return (
                <button
                  key={account.id}
                  type="button"
                  aria-current={isCurrent ? "true" : undefined}
                  aria-disabled={isCurrent ? "true" : undefined}
                  onClick={() => handleSwitch(slug)}
                  className={`list-group-item d-flex justify-content-between align-items-center${
                    isCurrent ? "" : " list-group-item-action"
                  }`}
                  style={{ cursor: isCurrent ? "default" : "pointer" }}
                >
                  <span>{account.attributes?.name}</span>
                  {isCurrent && (
                    <FontAwesomeIcon
                      icon={faCheck}
                      style={{ color: "#63C4AB" }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default SwitchAccountModal;
